// src/lib/blog/utils/formatting.ts

import { SPECIAL_CASES } from '~/lib/blog/config'

/**
 * Formats a tag or term for display, handling special cases and proper title case.
 * Only applies special case formatting when the word exactly matches a special case.
 *
 * @param {string} text - The text to format
 * @returns {string} Properly formatted text
 */
export function formatForDisplay(text: string): string {
  return text
    .split('-')
    .map(word => {
      const lowerWord = word.toLowerCase()

      // Only apply special cases for exact word matches
      if (Object.prototype.hasOwnProperty.call(SPECIAL_CASES, lowerWord)) {
        // Check if it's a complete word match, not a partial match
        const specialCase = SPECIAL_CASES[lowerWord]

        // If it's explicitly marked to be lowercase, keep it lowercase
        if (specialCase === lowerWord) {
          return lowerWord
        }

        return specialCase
      }

      // Default to title case for non-special words
      // Split into individual words to handle multi-word segments
      return word
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
    })
    .join(' ')
}

/**
 * Formats a tag for use in URLs by converting to lowercase, replacing spaces with hyphens,
 * and removing special characters.
 *
 * @param {string} tag - The tag to format for URL use
 * @returns {string} URL-safe tag
 * @throws {Error} If the tag parameter is null or undefined
 * @example
 * // Returns "hello-world"
 * formatForUrl("Hello World!")
 */
export function formatForUrl(tag: string): string {
  if (tag === null || tag === undefined) {
    throw new Error('Tag parameter cannot be null or undefined')
  }

  return tag
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/[^a-z0-9-]/g, '') // Only allow lowercase letters, numbers, and hyphens
    .replace(/-+/g, '-')      // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '')    // Remove leading/trailing hyphens
}

/**
 * Normalizes a tag for storage or comparison by converting to lowercase and trimming whitespace.
 *
 * @param {string} tag - The tag to normalize
 * @returns {string} Normalized tag
 * @throws {Error} If the tag parameter is null or undefined
 * @example
 * // Returns "hello world"
 * normalizeTag("  Hello World  ")
 */
export function normalizeTag(tag: string): string {
  if (tag === null || tag === undefined) {
    throw new Error('Tag parameter cannot be null or undefined')
  }

  return tag.toLowerCase().trim()
}
